// src/context/FormDataContext.jsx
import React, {createContext, useContext, useMemo, useState, useCallback, useEffect, useRef} from "react";
import { apiFetch as authApiFetch, logout as authLogout, API_BASE_URL } from "../auth/authClient";



// ✅ helper: z libovolné odpovědi (Response / JSON / {data:{raw:{formData}}}) vytáhne formData objekt
async function __unwrapFormDataAny(respOrData) {
  let d = respOrData;

  // Response -> json (VŽDY přes clone(), aby se nesežral původní body)
  try {
    if (d && typeof d === "object" && typeof d.json === "function") {
      const rr = (typeof d.clone === "function") ? d.clone() : d;
      d = await rr.json();
    }
  } catch (e) {
    console.warn("⚠️ __unwrapFormDataAny: json() failed", e);
  }

  // {data:...}
  const top = (d && typeof d === "object" && "data" in d) ? d.data : d;

  // {raw:...}
  const raw = (top && typeof top === "object" && top && "raw" in top) ? top.raw : top;

  // prefer raw.formData
  let fd =
    (raw && typeof raw === "object" && "formData" in raw) ? raw.formData :
    (top && typeof top === "object" && "formData" in top) ? top.formData :
    (d && typeof d === "object" && "formData" in d) ? d.formData :
    null;

  // fallback: někdy je formData rovnou top-level
  if (!fd && raw && typeof raw === "object" && (raw.vstupniInformace || raw.zesnulaOsoba)) fd = raw;
  if (!fd && top && typeof top === "object" && (top.vstupniInformace || top.zesnulaOsoba)) fd = top;

  // dot-keys -> nested
  const hasDot = (o) => {
    try { return !!o && typeof o === "object" && Object.keys(o).some(k => k.includes(".")); }
    catch { return false; }
  };

  if (fd && hasDot(fd)) {
    const out = {};
    for (const [k, v] of Object.entries(fd || {})) {
      if (!k.includes(".")) { out[k] = v; continue; }
      const parts = k.split(".");
      let cur = out;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (i === parts.length - 1) cur[part] = v;
        else {
          if (!cur[part] || typeof cur[part] !== "object") cur[part] = {};
          cur = cur[part];
        }
      }
    }
    fd = out;
  }

  return fd;
}




// -----------------------------
// 🔐 AUTH helper (pohrebka_token + fallbacky)
// -----------------------------
const getAuthToken = () => {
  try {
    const v =
      (localStorage.getItem("pohrebka_token") ||
       localStorage.getItem("token") ||
       localStorage.getItem("authToken") ||
       localStorage.getItem("accessToken") ||
       localStorage.getItem("jwt") ||
       "");
    return String(v || "").replace(/^Bearer\s+/i, "").trim();
  } catch (e) {
    return "";
  }
};
const getAuthHeaders = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// --------------------------------------------------
// CONTEXT
// --------------------------------------------------
const FormDataContext = createContext(null);

export function useFormData() {
  const ctx = useContext(FormDataContext);
  if (!ctx) throw new Error("useFormData() musí být použito uvnitř <FormDataProvider />");
  return ctx;
}

// --------------------------------------------------
// DEFAULT FORM DATA
// --------------------------------------------------
const DEFAULT_FORM_DATA = {
  pitvaTyp: "zdravotni",
  stavZakazky: "rozpracovana", // rozpracovana|hotova|storno
  vybraneDokumenty: [],
  slozkaSablon: "kremace", // kremace|pohreb

  vyjezd: {

    orderId: "",

    fotoOhledaciList: [],

    fotoZesnuly: [],

  },


  vyjezdy: {
    datumVyjezdu: "",
    casVyjezdu: "",
    zesnulyJmeno: "",
    mistoUmrti: "",
    datumUmrti: "",
    casUmrti: "",
    datumVyzvednuti: "",
    casVyzvednuti: "",
    posadka: "",
    spz: "",
    pitva: "", // "ano" | "ne"
    poznamka: "",
    ocrRawText: "",
  },

  vstupniInformace: {

    datumVystaveni: "",
    datumUmrti: "",
    casUmrti: "",
    mistoUmrti: "",
    adresaUmrti: "",
    poznamka: "",
    jmenoZesnuleho: "",
    prijmeniZesnuleho: "",
    datumNarozeniZesnuleho: "",
    adresaZesnuleho: "",
    trvaleBydliste: "",
    adresaTrvalehoBydliste: "",
    typRakve: "",
    rodneCislo: "",
    datumProhlidky: "",
    casProhlidky: "",
    pitevna: "",
    lekarJmeno: "",
    lekarTelefon: "",
    diagnoza: "",
    infekce: "",

      identita: {
        // ✅ checkbox "Údaje dle dokladu" – OP ≠ realita
        udajeDleDokladu: true,

        statniPrislusnost: "ČR",

        // ✅ vždy vyplnitelné (i když je ČR – narození může být SK/USA atd.)
        mistoNarozeni: {
          obec: "",
          cast: "",
          okres: "",
          stat: "",
        },

        trvaleBydliste: "",
        prechodneBydliste: "",

        poznamka: "",

        // ✅ OP / PAS / ŘP / Jiný + číslo dokladu vždy
        doklad: {
          typ: "OP", // OP | PAS | RP | JINY
          cislo: "",
          soubory: {
            predni: null,        // OP/RP
            zadni: null,         // OP/RP
            pas: null,           // PAS
            rodnyList: null,     // volitelné
            ohledaciList: null,  // volitelné
          },
        },
      },


},

  identita: {
    statniPrislusnost: "ČR",
    mistoNarozeni: {
      obec: "",
      cast: "",
      okres: "",
      stat: "ČR",
    },
    trvaleBydliste: "",
    prechodneBydliste: "",
    doklady: {
      opPredni: null,
      opZadni: null,
      rodnyList: null,
      ohledaciList: null,
    },
  },

  zesnulaOsoba: {
    jmeno: "",
    prijmeni: "",
    datumNarozeni: "",
    datumUmrti: "",
    trvaleBydliste: "",
    adresaTrvalehoBydliste: "",
    adresa: "",
    mistoUmrti: "",
    rodneCislo: "",
  },

  
vypravitel: {

  jmeno: "",
  prijmeni: "",
  vztah: "",
  datumNarozeni: "",
  adresa: "",
  telefon: "",
  email: "",
  statniPrislusnost: "ČR",
  opObjednavatele: {
    predni: null,
    zadni: null,
    },  kontaktniOsoba: {
    jmeno: "",
    prijmeni: "",
    telefon: "",
    email: "",
    trvaleBydliste: "",
    prechodneBydliste: "",
    },

      


},
  rec: {
    rodina: {
      partner: "",
      deti: "",
      vnucata: "",
      sourozenci: "",
      rodice: "",
      dalsi: "",
    },
    zivot: {
      puvod: "",
      detstvi: "",
      skola: "",
      prace: "",
      zajmy: "",
      hodnoty: "",
    },
    charakter: {
      povaha: "",
      vlastnosti: "",
      zvyky: "",
      vzpominky: "",
      urciteZminit: "",
    },
    citlive: {
      nezminovat: "",
      opatrne: "",
      poznamka: "",
    },
    forma: {
      delka: "",
      styl: "",
      nabozenskyTon: "",
      dulezityDuraz: "",
      zaver: "",
    },
    text: {
      poznamky: "",
      navrhReci: "",
    },
  },


  // ✅ tady držíme “zdrojové” obřadové údaje z Kremace/Pohřeb
  kremacePohreb: {
    typ: "Kremace",
    obradTyp: "",
    obradDatum: "",
    obradCas: "",
    obradPosadka: "",
    mistoObradu: "",
    // aliasy (kvůli starším částem appky – nevadí, když zůstanou)
    datumObradu: "",
    casObradu: "",
    typObradu: "",
  },

  // ✅ toto používá Kalendář obřadů (předvyplnění)
  obrad: {
    posadka: "",
    typ: "",
    datum: "",
    cas: "",
    
      casOd: "",
      casDo: "",
vystav: "",
  },

  rakve: {
    typRakve: "",
    oholit: "",
    upominka: "",
    poznamka: "",
    soupravaBarva: "",
    soupravaSmer: "", // "dovnitr" | "ven"
  },

  predaniCB: {
    datumPredani: "",
    hodinaPredani: "",
    poznamka: "",
  },

  vybranaRakveId: "",
  skladRakvi: [],

  sluzby: [],
  matrika: {},
  kvetiny: {},
  prevoz: {
    baseAdresa: "Český Krumlov, Pod Skalkou 525",
    ckChladAdresa: "Český Krumlov, Hřbitovní 149",
    autoBuild: true,   // auto předvyplnění z výjezdů/obřadu/kremace (postupně)
    autoCalc: true,    // auto dopočet km přes API
    totalKm: 0,
  },
  prevozy: [],
};

function deepClone(obj) {
  try {
    return JSON.parse(JSON.stringify(obj ?? {}));
  } catch (e) {
    return obj ?? {};
  }
}

// 🧪 TRACE helper: loguje jen změny orderId/zakazkaId/id (a jen když se změní)
function __traceOrderKeysDiff(prev, next, label) {
  try {
    const p = prev || {};
    const n = next || {};
    const keys = ["orderId", "zakazkaId", "id"];
    const changes = [];
    for (const k of keys) {
      const a = String(p[k] ?? "");
      const b = String(n[k] ?? "");
      if (a !== b) changes.push({ key: k, from: a, to: b });
    }
    if (!changes.length) return;
    console.table(changes);
    console.trace("stack:");
  } catch (e) {}
}


function deepMerge(target, source) {
  const t = target && typeof target === "object" ? target : {};
  const s = source && typeof source === "object" ? source : {};
  const out = Array.isArray(t) ? [...t] : { ...t };

  for (const [k, v] of Object.entries(s)) {
    if (Array.isArray(v)) out[k] = v;
    else if (v && typeof v === "object") out[k] = deepMerge(t[k], v);
    else out[k] = v;
  }
  return out;
}

/**
 * Token helper – robustně, protože někde máš "pohrebka_token", jinde může být "token" apod.
 */
function getAnyToken() {

    // ✅ Po loginu/refreshi NIKDY automaticky neotevírej poslední zakázku (aby se nepřepisovala historie)
  // Zakázku musí uživatel vybrat / nebo se založí nová až při explicitním uložení.
    // ✅ Po loginu/refreshi: žádná zakázka není v editaci dokud ji uživatel explicitně neotevře
    // ✅ Po loginu/refreshi: žádná zakázka není v editaci dokud ji uživatel explicitně neotevře
return (
    localStorage.getItem("pohrebka_token") ||
    localStorage.getItem("pohrebkaToken") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

// --------------------------------------------------
// ✅ JEDINÉ API: přes authClient + jednotné helpery + 401 logout
// --------------------------------------------------
async function apiFetchSafe(path, options = {}) {
  try {
    return await authApiFetch(path, options);
  } catch (e) {
    if (e?.status === 401) authLogout();
    throw e;
  }
}

  async function apiGet(path) {
    // ✅ musí vracet Response (kvůli existujícím call-site, které volají resp.json())
    return apiFetchSafe(path, { method: "GET" });
  }

  // ✅ NOVÉ: když chceš JSON-wrapper, používej tohle (neměníme význam apiGet)
  async function apiGetWrap(path) {
    const r = await apiFetchSafe(path, { method: "GET" });

    // když to není Response, zabalíme (pro jistotu)
    if (!r || typeof r !== "object" || typeof r.clone !== "function" || typeof r.status !== "number") {
      return { ok: true, status: 200, data: r, error: "", rawResponse: null };
    }

    let data = null;
    let rawText = "";

    try {
      data = await r.clone().json();
    } catch (e1) {
      try {
        rawText = await r.clone().text();
        try { data = JSON.parse(rawText); }
        catch { data = { rawText }; }
      } catch (e2) {
        data = { error: String(e2?.message || e2) };
      }
    }

    const ok = !!r.ok;
    const status = Number(r.status || 0);

    let error = "";
    if (!ok) {
      error =
        (data && typeof data === "object" && (data.error || data.message)) ? String(data.error || data.message) :
        rawText ? String(rawText).slice(0, 400) :
        `HTTP ${status}`;
    }

    return { ok, status, data, error, rawResponse: r };
  }



async function apiGetJson(path, token) {
  const url = apiUrl(path);
  const r = await fetch(url, {
    method: "GET",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
  return j;
}



async function apiPostJson(path, bodyObj) {
  return apiFetchSafe(path, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(bodyObj ?? {}),
  });
}

async function apiDelete(path) {
  return apiFetchSafe(path, { method: "DELETE" });
}

/**
 * Pro ZIP/PDF/Blob odpovědi – fetch ručně, ale:
 * - použije stejný API_BASE_URL
 * - přidá token
 * - při 401 udělá logout
 */
async function apiPostBlob(path, bodyObj) {
  const token = getAnyToken();
  const url = `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;

  const r = await fetch(url, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(bodyObj ?? {}),
  });

  if (r.status === 401) {
    authLogout();
    const txt = await r.text().catch(() => "");
    throw new Error(txt || "401 Unauthorized");
  }

  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(txt || `Server chyba ${r.status}`);
  }

  return r;
}


// ===============================
// LOCK: SOURCE OF TRUTH pro obřad je Kremace/Pohřeb
// (kremacePohreb.datumObradu/casObradu, případně fallback obradDatum/obradCas)
// ===============================
function normalizeObradFromKP(fd) {
  const next = { ...(fd || {}) };
  const kp = next?.kremacePohreb || {};
  const ob = next?.obrad || {};

  const kpDatum = String(kp?.datumObradu || kp?.obradDatum || "").trim();
  const kpCas   = String(kp?.casObradu   || kp?.obradCas   || "").trim();

  // když KP nemá datum ani čas, NIC netlačíme (může být jen převoz / bez obřadu)
  if (!kpDatum && !kpCas) return next;

  next.obrad = {
    ...(ob || {}),
    // sjednocené klíče (nové)
    datum: kpDatum || String(ob?.datum || "").trim(),
    casOd: kpCas || String(ob?.casOd || ob?.cas || "").trim(),
    // casDo necháme jak je (může být nastavené jinde / automaticky)
    casDo: String(ob?.casDo || "").trim(),

      // posádka (zachovat při LOCK normalizaci)
      posadka: String(ob?.posadka || next?.obradPosadka || "").trim(),

    // kompatibilita (starší čtení)
    cas: kpCas || String(ob?.cas || "").trim(),
    datumObradu: kpDatum || String(ob?.datumObradu || "").trim(),
    casObradu: kpCas || String(ob?.casObradu || "").trim(),
  };

  // root kompatibilita (když někde čteš formData.datumObradu/casObradu)
  if (kpDatum) next.datumObradu = kpDatum;
  if (kpCas) next.casObradu = kpCas;

  return next;
}



  // ===============================
  // NORMALIZE: legacy data z /api/kniha/nacist -> tvar co UI očekává
  // - obřad datum: když je uložené jako top-level datum, doplň do kremacePohreb + obrad
  // - služby: když mají jen "kod", doplň "id/key" (jinak UI nepozná vybrané)
  // ===============================
  function normalizeLoadedFormData(fd) {
    const next = (fd && typeof fd === "object") ? { ...fd } : {};

    // --- služby (kod -> id/key) ---
    const sl = Array.isArray(next.sluzby) ? next.sluzby : [];
    next.sluzby = sl.map((x) => {
      if (!x || typeof x !== "object") return x;
      const kod = String(x.kod || x.id || x.key || "").trim();
      if (!kod) return x;
      return {
        ...x,
        id: x.id ?? kod,
        key: x.key ?? kod,
      };
    });

    // --- obřad datum (legacy: next.datum) -> kremacePohreb/obrad ---
    const legacyDatum = String(next.datum || "").trim();
    const kp = (next.kremacePohreb && typeof next.kremacePohreb === "object") ? { ...next.kremacePohreb } : {};
    const ob = (next.obrad && typeof next.obrad === "object") ? { ...next.obrad } : {};

    const kpDatum = String(kp.datumObradu || kp.obradDatum || "").trim();
    if (legacyDatum && !kpDatum) {
      kp.obradDatum = legacyDatum;
      kp.datumObradu = legacyDatum;
    }

    // obrad.datum taky doplň, ať ObřadForm má co zobrazit (i kdyby nejel LOCK)
    const obDatum = String(ob.datum || "").trim();
    if (legacyDatum && !obDatum) ob.datum = legacyDatum;

    next.kremacePohreb = kp;
    next.obrad = ob;

    return next;
  }

export function FormDataProvider({ children }) {

  // === AUTO: listen pohrebka:orderPatched (Vyjezdy STEP2) ===
  const [formData, _setFormDataRaw] = useState(() => deepClone(DEFAULT_FORM_DATA));
// ✅ alias pro starší kód (setFormData používá _setFormData)
  const _setFormData = _setFormDataRaw;

  // === AUTO: formDataRef + global debug handle (for console inspection) ===
    // === AUTO: formDataRef + global debug handle (for console inspection) ===
  const formDataRef = useRef(null);
  useEffect(() => {
    try { formDataRef.current = formData; } catch {}
    try {
      // stabilní debug API (funguje i když se proměnné přepisují)
      window.__POHREBKA_DEBUG__ = {
        getFormData: () => formDataRef.current,
        getOrderId: () => (formDataRef.current && (formDataRef.current.orderId || formDataRef.current.id || formDataRef.current.zakazkaId)) || "",
      };
      // kompatibilita: starý přímý pointer
      window.__POHREBKA_DEBUG_FORMDATA = formDataRef.current;
    } catch {}
  }, [formData]);

// === SAFE FORMDATA WRAPPERS (TEMP DEBUG) ===
  const __fdDbg = (label, extra = {}) => {
  try {
      // ✅ PLAIN DEBUG (vždy viditelné v prod konzoli)
      const r = (extra && extra.reason) ? String(extra.reason) : "";
      const pk = (extra && extra.patchKeys) ? extra.patchKeys : [];
      const oid = (extra && extra.orderId) ? String(extra.orderId) : "";
      if (label && (String(label).includes("MERGE") || String(label).includes("SET"))) {
      }
    } catch (e) {}
try {
        const wantStack = String(label || "").includes("MERGE(real)");
      } catch {}
    };

  const deepMerge = (a, b) => {
    if (Array.isArray(a) || Array.isArray(b)) return (b ?? a);
    if (a && typeof a === "object" && b && typeof b === "object") {
      const out = { ...a };
      for (const k of Object.keys(b)) out[k] = deepMerge(a[k], b[k]);
      return out;
    }
    return (b === undefined) ? a : b;
  };

  const shouldBlockSuspiciousOverwrite = (prev, nextVal) => {
    const prevKeys = Object.keys(prev || {}).length;
    const nextKeys = Object.keys(nextVal || {}).length;
    if (prevKeys >= 10 && nextKeys > 0 && nextKeys < Math.max(5, Math.floor(prevKeys * 0.4))) {
      return { prevKeys, nextKeys };
    }
    return null;
  };

  // ✅ MERGE helper – používat pro LOAD/AUTOSAVE
  const dbgMergeFormData = (patch, reason = "merge") => {
    _setFormDataRaw(prev => {
      const patchVal = (typeof patch === "function") ? patch(prev) : patch;
      const nextVal = deepMerge(prev || {}, patchVal || {});
        // 🔒 DEDUPE: pokud se zapisuje stejný obsah, vrať původní referenci => stop render loop
        try {
          if (nextVal && typeof nextVal === "object") {
            const a = JSON.stringify(prev || {});
            const b = JSON.stringify(nextVal || {});
            if (a === b) return prev;
          }
        } catch (e) {}

      __fdDbg("🧩 MERGE", {
        reason,
        patchKeys: Object.keys(patchVal || {}),
        orderId: nextVal?.meta?.orderId ?? nextVal?.vyjezd?.orderId ?? nextVal?.orderId ?? null,
      });
      return nextVal;
    });
  };

  // ✅ SAFE setFormData – zachová jméno, ale hlídá full overwrite
  const dbgSetFormData = (next, reason = "set") => {
    _setFormDataRaw(prev => {
      const nextVal = (typeof next === "function") ? next(prev) : next;
        // 🔒 DEDUPE_SET: pokud se zapisuje stejný obsah, vrať původní referenci => stop render loop
        try {
          const a = JSON.stringify(prev || {});
          const b = JSON.stringify(nextVal || {});
          if (a === b) return prev;
        } catch (e) {}


      const blocked = shouldBlockSuspiciousOverwrite(prev, nextVal);
      if (blocked && !/reset|new|nova|nová|init/i.test(String(reason))) {
        console.warn("⛔ BLOCKED suspicious overwrite", { reason, ...blocked, nextVal });
        return prev;
      }

      __fdDbg("🧨 SET", {
        reason,
        nextKeys: Object.keys(nextVal || {}).length,
        orderId: nextVal?.meta?.orderId ?? nextVal?.vyjezd?.orderId ?? nextVal?.orderId ?? null,
      });
      return nextVal;
    });
  };

  // ✅ Log každého update
  useEffect(() => {
    const keys = Object.keys(formData || {});
    console.log("🧠 FORMDATA UPDATE", {
      t: new Date()
.toISOString(),
      keysCount: keys.length,
      orderId: formData?.meta?.orderId ?? formData?.vyjezd?.orderId ?? formData?.orderId ?? formData?.zakazkaId ?? formData?.id ?? null,
    });
  }, [formData]);
  // === END SAFE FORMDATA WRAPPERS ===



  // DEBUG: zpřístupní formData v prohlížeči (F12 → Console)
  // v produkci klidně smažeme
  useEffect(() => {
    try {

    } catch {}
  }, [formData]);

  

  // ✅ LOCKED setter: každý update srovná obřad podle K/P (source of truth)
  const setFormData = useCallback((updater, reason = "set") => {
    _setFormDataRaw((prev) => {
      let next =
        typeof updater === "function"
          ? updater(prev)
          : { ...(prev || {}), ...(updater || {}) };
      // ✅ AUTO: ensure formData IDs from currentOrderId (aby nebyl mismatch)

      try {

          const __reason = String(reason || "");
          const __skipIdSyncForOpen =
            /openZakazkaById/i.test(__reason) ||
            /replace:openZakazkaById/i.test(__reason);

          const coid = String(currentOrderId || "");

          if (!__skipIdSyncForOpen && coid) {

            const nid = String((next?.orderId ?? next?.zakazkaId ?? next?.id ?? next?.vyjezd?.orderId ?? "") || "");

            if (!nid || nid !== coid) {

              next = {

                ...(next || {}),

                orderId: coid,

                zakazkaId: coid,

                id: coid,

                vyjezd: { ...((next && next.vyjezd) || {}), orderId: coid },

              };

            }

          }

        } catch (e) {}

      __traceOrderKeysDiff(prev, next, reason || 'setFormData');

      try {
        const blocked = __fdShouldBlockOverwrite(prev, next);
          const __reasonTxt = String(reason || "");
          const __allowOpenReplace =
            /openZakazkaById/i.test(__reasonTxt) ||
            /replace:openZakazkaById/i.test(__reasonTxt);

          if (blocked && !__allowOpenReplace && !/reset|new|nova|nová|init/i.test(__reasonTxt)) {
            console.warn("⛔ BLOCKED suspicious overwrite", { reason, ...blocked, next });
            return prev;
          }
        __fdDbg("🧨 SET", {
          reason,
          prevKeys: Object.keys(prev || {}).length,
          nextKeys: Object.keys(next || {}).length,
          orderId: __fdOrderId(next),
        });
      } catch (e) {}
      // === END FD INSTRUMENT ===

      
        // 🔒 DEDUP_NORM_OBRAD: normalizeObradFromKP může vracet nové reference i při stejném obsahu
        // => pokud obsah odpovídá prev, vrať prev (STOP render loop)
        const __norm = normalizeObradFromKP(next);
        try {
          const a = JSON.stringify(prev || {});
          const b = JSON.stringify(__norm || {});
          if (a === b) return prev;
        } catch (e) {}
        return __norm;

    });
  }, []);const [currentOrderId, _setCurrentOrderId] = useState(() => {
  try {
    return (
      localStorage.getItem("lastOpenedOrderId") ||
      ""
    );
  } catch {
    return "";
  }
});

  // 🧪 TRACE: když je currentOrderId nastavené, ale formData nenese žádné ID (nebo nese jiné)
    const __lastOpenTsRef = useRef(0);

  const __lastMismatchRef = useRef("");
  useEffect(() => {
    try {
      const coid = String(currentOrderId ?? "");
      if (!coid) return;

      const fd = formData || {};
      const fdId = String(
        (fd.orderId ?? fd.zakazkaId ?? fd.id ?? (fd.vyjezd && fd.vyjezd.orderId) ?? "")
      );

      const key = coid + "||" + fdId;
      if (__lastMismatchRef.current === key) return;

      if (!fdId || fdId !== coid) {
        __lastMismatchRef.current = key;
        const __fdId = String(formData?.orderId || formData?.id || formData?.zakazkaId || "");
        const __coid = String(currentOrderId || "");
        const __cooldown = Date.now() - (__lastOpenTsRef?.current || 0);
        if (isOpeningOrder) {
          return;
        }
        if (!__coid || !__fdId) {
          return;
        }
        if (__cooldown >= 0 && __cooldown < 600) {
          return;
        }
        console.trace("stack:");
      }
    } catch (e) {}
  }, [
    currentOrderId,
    formData?.orderId,
    formData?.zakazkaId,
    formData?.id,
    formData?.vyjezd?.orderId
  ]);

  // 🧨 TRACE: wrapper pro setCurrentOrderId – ukáže stack PŘÍMO z místa volání
  const setCurrentOrderId = useCallback((next, reason = "") => {
    try {
      const v = String(next || "").trim();
      console.trace("stack:");
    } catch (e) {}
    
      // ✅ AUTO: sync formData IDs on setCurrentOrderId (aby hned zmizel mismatch po open)
      try {
        const __z = String(next || "").trim();
        if (__z) {
          _setFormDataRaw((prev) => {
            const p = prev || {};
            const cur = String((p.orderId ?? p.zakazkaId ?? p.id ?? (p.vyjezd && p.vyjezd.orderId) ?? "") || "");
            if (cur === __z) return p;
            return {
              ...p,
              orderId: __z,
              zakazkaId: __z,
              id: __z,
              vyjezd: { ...((p && p.vyjezd) || {}), orderId: __z },
            };
          });
        }
      } catch (e) {}

      // 🔒 GUARD: nedovol "boot/restore" přepsat currentOrderId na jiný než je formData (zabrání rollback 181 -> 180)
      try {
        const nextId = String(next || "").trim();
        const fd = (typeof formDataRef !== "undefined" && formDataRef && formDataRef.current) ? formDataRef.current : {};
        const fdId = String((fd?.orderId || fd?.zakazkaId || fd?.id || "") || "").trim();
        const r = String(reason || "");
        const isSoft = (r === "" || r.includes("boot") || r.includes("restore") || r.includes("init") || r.includes("mount") || r.includes("loadLS"));
        if (nextId && fdId && nextId !== fdId && isSoft) {
          try { console.warn("⛔ setCurrentOrderId ignored (would desync)", { nextId, fdId, reason: r }); } catch {}
          return;
        }
      } catch {}
      _setCurrentOrderId(v);
      // LS_SYNC_CURRENT_ORDERID
      try {
        const _id = String(v || "").trim();
        if (_id) {
          localStorage.setItem("currentOrderId", _id);
          localStorage.setItem("lastOpenedOrderId", _id);
        } else {
          localStorage.removeItem("currentOrderId");
          localStorage.removeItem("lastOpenedOrderId");
        }
      } catch {}

      // ✅ SYNC: also write into formData orderId/id/zakazkaId (prevents ORDERID MISMATCH)
      try {
        const vSync = String(next || "").trim();
        if (vSync && typeof _setFormDataRaw === "function") {
          _setFormDataRaw((prev) => {
            const prevObj = (prev && typeof prev === "object") ? prev : {};
            const cur = String(prevObj.orderId || prevObj.zakazkaId || prevObj.id || "").trim();
            if (cur === vSync && prevObj.orderId === vSync && prevObj.id === vSync && prevObj.zakazkaId === vSync) return prevObj;
            return { ...prevObj, orderId: vSync, id: vSync, zakazkaId: vSync };
          });
        }
      } catch {}


  }, [currentOrderId]);

    // 🧪 DEBUG/TRACE: odhal kdy a odkud se currentOrderId vynuluje
  const __prevOrderIdRef = React.useRef("");
  useEffect(() => {
    try {
      const prev = String(__prevOrderIdRef.current || "");
      const cur = String(currentOrderId || "");
      if (prev && !cur) {
        console.trace("stack:");
      }
      __prevOrderIdRef.current = cur;

      try {
        window.__POHREBKA_DEBUG_FORMDATA = {
          currentOrderId: cur,
          prevOrderId: prev,          hasToken: !!(localStorage.getItem("pohrebka_token") || localStorage.getItem("token")),
          t: new Date().toISOString(),
        };
      } catch (e) {}
    } catch (e) {}
  }, [currentOrderId]);



  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [isOpeningOrder, setIsOpeningOrder] = useState(false);
  // =============================
  // SAVEFLOW_V2
  // =============================
  const [lastSavedJson, setLastSavedJson] = useState(() => {
    try { return localStorage.getItem("lastSavedJson") || ""; } catch (e) { return ""; }
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const safeJson = useCallback((obj) => {
    try { return JSON.stringify(obj || {}); } catch (e) { return ""; }
  }, []);
  // ✅ helper: applyObradMetaToOrder (nutné pro ctxValue/useMemo)
  // Bezpečná verze: vrátí order + případně doplní obřadové meta do obrad/kremacePohreb
  function applyObradMetaToOrder(order, meta) {
    const base = (order && typeof order === "object") ? order : {};
    const m = (meta && typeof meta === "object") ? meta : {};

    const posadkaNazev = String(m.posadkaNazev || m.posadka || "").trim();
    const autoSpz = String(m.autoSpz || "").trim();
    const datum = String(m.datum || m.datumObradu || "").trim();
    const casOd = String(m.casOd || m.cas || m.casObradu || "").trim();
    const casDo = String(m.casDo || "").trim();
    const misto = String(m.misto || "").trim();
    const typ = String(m.typ || "").trim();

    const patch = {
      obrad: {
        ...(base?.obrad || {}),
        ...(posadkaNazev ? { posadkaNazev, posadka: posadkaNazev } : {}),
        ...(autoSpz ? { autoSpz } : {}),
        ...(datum ? { datum, datumObradu: datum } : {}),
        ...(casOd ? { casOd } : {}),
        ...(casDo ? { casDo } : {}),
        ...(misto ? { misto } : {}),
        ...(typ ? { typ } : {}),
      },
      kremacePohreb: {
        ...(base?.kremacePohreb || {}),
        ...(posadkaNazev ? { obradPosadka: posadkaNazev } : {}),
        ...(datum ? { obradDatum: datum, datumObradu: datum } : {}),
        ...(casOd ? { obradCas: casOd, casObradu: casOd } : {}),
      },
    };

    // deepMerge je v tomhle souboru už používaný – využijeme ho
    try {
      return deepMerge(base || {}, patch || {});
    } catch (e) {
      // fallback kdyby deepMerge nebyl v scope (nemělo by nastat)
      return { ...(base || {}), ...(patch || {}) };
    }
  }


  const isDirty = useMemo(() => {
    if (!currentOrderId) return false;
    const cur = safeJson(formData);
    return !!(lastSavedJson && cur && cur !== lastSavedJson);
  }, [currentOrderId, formData, lastSavedJson, safeJson]);

  // (vypnuto) currentOrderId se už neukládá do localStorage – po loginu nesmí být aktivní stará zakázka

  useEffect(() => { try { localStorage.setItem("lastSavedJson", String(lastSavedJson || "")); } catch (e) {} }, [lastSavedJson]);

  const [skladLoading, setSkladLoading] = useState(false);
  const [skladError, setSkladError] = useState("");

  // -----------------------------
  // BASIC
  // -----------------------------
  const resetForm = useCallback(() => {
      setFormData(deepClone(DEFAULT_FORM_DATA));
      setCurrentOrderId("");
      setIsEditingExisting(false);
      setLastSavedJson("");
      setSaveError("");
      setSkladLoading(false);
      setSkladError("");
      try { localStorage.removeItem("lastSavedJson"); } catch (e) {}
    }, []);

  const mergeFormData = useCallback((patch, reason = "merge") => {
      try {
        __fdDbg("🧩 MERGE(real)", {
          reason,
          patchKeys: Object.keys(patch || {}),
          orderId: formData?.meta?.orderId ?? formData?.vyjezd?.orderId ?? formData?.orderId ?? null,
        });
      } catch (e) {}

      setFormData((prev) => {
          const __prev = (prev && typeof prev === "object") ? prev : {};
          const __patch = (patch && typeof patch === "object") ? patch : {};

          // pokud patch vůbec nesahá na klíčové ID, nesmí se změnit
          const __touches =
            ("orderId" in __patch) || ("id" in __patch) || ("zakazkaId" in __patch) ||
            (__patch.meta && (("orderId" in __patch.meta) || ("id" in __patch.meta) || ("zakazkaId" in __patch.meta))) ||
            (__patch.vyjezd && (("orderId" in __patch.vyjezd) || ("id" in __patch.vyjezd) || ("zakazkaId" in __patch.vyjezd))) ||
            (__patch.vyjezdy && (("orderId" in __patch.vyjezdy) || ("id" in __patch.vyjezdy) || ("zakazkaId" in __patch.vyjezdy)));

          const __merged = deepMerge(__prev || {}, __patch || {});

          if (!__touches) {
            const __keep = String(
              __prev?.meta?.orderId ??
              __prev?.orderId ??
              __prev?.zakazkaId ??
              __prev?.id ??
              ""
            ).trim();

            if (__keep) {
              __merged.orderId = __keep;
              __merged.zakazkaId = __prev?.zakazkaId ?? __keep;
              __merged.id = __prev?.id ?? __keep;
              const __m = (__merged.meta && typeof __merged.meta === "object") ? __merged.meta : {};
              __merged.meta = { ...__m, orderId: __keep, zakazkaId: (__m.zakazkaId ?? __merged.zakazkaId), id: (__m.id ?? __merged.id) };
            }
          }

          return __merged;
        }, "merge:" + String(reason || ""));}, [formData, setFormData]);


  

  // -----------------------------
  // 🔒 SAFE setFormData (deep-merge)
  // - když někdo zavolá ctx.setFormData({ jen část ... }), nesmí to smazat ostatní záložky
  // - full replace povolíme jen přes { __replace: true, ... }
  // -----------------------------
  // ✅ helper: __forceIds
  // Drží konzistenci orderId/zakazkaId/id v celém formData (ochrana proti „přelívání“ mezi zakázkami)
  
function __forceIds(obj) {
  const o = (obj && typeof obj === "object") ? obj : {};

  // vezmeme ID z nejspolehlivějších míst
  const forced = String(
    o?.meta?.orderId ??
    o?.vyjezd?.orderId ??
    o?.vyjezdy?.orderId ??
    o?.orderId ??
    o?.zakazkaId ??
    o?.id ??
    ""
  ).trim();

  if (!forced) return o;

  const meta = (o.meta && typeof o.meta === "object") ? o.meta : {};

  // vyjezd: VŽDY přepiš orderId na forced (nesmí zůstat staré)
  const vyjezd =
    (o.vyjezd && typeof o.vyjezd === "object")
      ? { ...o.vyjezd, orderId: forced, zakazkaId: forced }
      : o.vyjezd;

  // vyjezdy: může být objekt nebo pole
  let vyjezdy = o.vyjezdy;
  if (Array.isArray(vyjezdy)) {
    vyjezdy = vyjezdy.map(v =>
      (v && typeof v === "object")
        ? { ...v, orderId: forced, zakazkaId: forced }
        : v
    );
  } else if (vyjezdy && typeof vyjezdy === "object") {
    vyjezdy = { ...vyjezdy, orderId: forced, zakazkaId: forced };
  }

  return {
    ...o,
    orderId: forced,
    zakazkaId: forced,
    id: forced,
    meta: { ...meta, orderId: forced, id: forced, zakazkaId: forced },
    vyjezd,
    vyjezdy,
  };
}



  const safeSetFormData = useCallback((next, reason) => {
  // ✅ __replace:true => FULL REPLACE (reset na DEFAULT + merge jen načtená data)
  const __doReplace = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    if (obj.__replace !== true) return null;
    const o = { ...obj };
    delete o.__replace;
    const base = deepClone(DEFAULT_FORM_DATA);
    return __forceIds(deepMerge(base || {}, o || {}));
  };

  // funkční updater
  if (typeof next === "function") {
    return setFormData((prev) => {
      const computed = next(prev);
      const rep = __doReplace(computed);
      if (rep) return rep;

      if (computed && typeof computed === "object") {
        return __forceIds(deepMerge(prev || {}, computed || {}));
      }
      if (computed == null) return prev;
      return computed;
    }, reason);
  }

  // objekt / replace
  if (next && typeof next === "object") {
    const rep = __doReplace(next);
    if (rep) return setFormData(rep, "replace:" + String(reason || ""));
    return setFormData((prev) => __forceIds(deepMerge(prev || {}, next || {})), reason);
  }

  if (next == null) return setFormData((prev) => prev, reason);
  return setFormData(next, reason);
}, [setFormData]);
// -----------------------------
  // ✅ OPEN ZAKAZKA
  // -----------------------------
    // 🔒 OPEN RACE GUARD (zabrání přepsání pozdní odpovědí)
    const openSeqRef = useRef(0);
      const openZakazkaById = useCallback(async (id) => {
      // OPEN_CALLSTACK_TRACE
      try {
        const __zid_dbg = String(id || "").trim();
        const __stk = (new Error("openZakazkaById caller")).stack;
      } catch {}

    const zid = String(id || "").trim();
    if (!zid) return { ok: false, error: "Missing id" };

      try { setIsOpeningOrder(true); } catch {}

    // 🔒 anti-race: aplikuj pouze poslední open
    const seq = ++openSeqRef.current;
    const targetId = zid;

    try {

  const resp = await apiGet(`/api/kniha/nacist/${encodeURIComponent(zid)}`);

  
      const fd = await __unwrapFormDataAny((resp && typeof resp === "object" && "data" in resp) ? resp.data : resp);
// (debug off)       console.log("🧪 OPEN fd(keys)", fd ? Object.keys(fd) : null);
// (debug off)       console.log("🧪 OPEN fd.vstupniInformace", fd?.vstupniInformace);
    // ✅ jediný zdroj pravdy pro loadedObj je fd
    const loadedObj = (fd && typeof fd === "object") ? fd : {};
      // ✅ CANONICAL ID: ber ID z dat (orderId/meta.orderId/zakazkaId), fallback na request zid
      // ✅ CANONICAL ID: vždy respektuj requested id (zid). Data můžou mít uvnitř špatné/stará ID.
        const __loadedId =
          String(
            (loadedObj && (
              loadedObj.orderId ||
              loadedObj.zakazkaId ||
              loadedObj.id ||
              (loadedObj.meta && (loadedObj.meta.orderId || loadedObj.meta.zakazkaId || loadedObj.meta.id))
            )) || ""
          ).trim();

        if (__loadedId && __loadedId !== zid) {
          try { console.warn("⚠️ OPEN loaded data contains different id (will override to requested zid)", { zid, loadedId: __loadedId }); } catch {}
        }

        const canonicalId = zid;

      // 🔒 stale response guard (anti-race) – aplikuj jen poslední open
      if (seq !== openSeqRef.current) {
          try { console.warn("⏭️ OPEN ignored (stale seq)", { zid, seq, cur: openSeqRef.current }); } catch {}
          return { ok: false, id: zid, ignored: true, stale: true };
        }

        // ✅ OPEN APPLY (REPLACE):
        // - vždy přepiš formData na DEFAULT + loaded
        // - vždy drž ID = requested zid (už canonicalId=zid)
        try { setCurrentOrderId(zid, "openZakazkaById"); } catch {}
        try { setIsEditingExisting(true); } catch {}          // === FIX: define hydrated (clone) + force canonical ids ===
          const __src = (loadedObj && typeof loadedObj === "object") ? loadedObj : {};
          const hydrated =
            (typeof structuredClone === "function")
              ? structuredClone(__src)
              : JSON.parse(JSON.stringify(__src || {}));

          try {
            hydrated.meta = (hydrated.meta && typeof hydrated.meta === "object") ? hydrated.meta : {};
            hydrated.meta.orderId = canonicalId;
            hydrated.orderId = canonicalId;
            hydrated.zakazkaId = canonicalId;
            hydrated.id = canonicalId;
          } catch {}
          // === /FIX ===// (removed) // (removed) // (removed duplicate) setCurrentOrderId(zid, "openZakazkaById");
        try {
        } catch {}

      
      // ✅ Auto-fill: pokud je vyplněný "vyjezdy", ale "vstupniInformace/zesnulaOsoba" jsou prázdné,
      // doplň základní údaje (bez přepsání toho, co už vyplněné je).
      try {
        const v = hydrated?.vyjezdy && typeof hydrated.vyjezdy === "object" ? hydrated.vyjezdy : null;
        const vi = hydrated?.vstupniInformace && typeof hydrated.vstupniInformace === "object" ? hydrated.vstupniInformace : {};
        const zo = hydrated?.zesnulaOsoba && typeof hydrated.zesnulaOsoba === "object" ? hydrated.zesnulaOsoba : {};

        const viEmpty =
          !String(vi?.jmenoZesnuleho || "").trim() &&
          !String(vi?.prijmeniZesnuleho || "").trim() &&
          !String(vi?.rodneCislo || "").trim();

        const zoEmpty =
          !String(zo?.jmeno || "").trim() &&
          !String(zo?.prijmeni || "").trim() &&
          !String(zo?.rodneCislo || "").trim();

        if (v && (viEmpty || zoEmpty)) {
          // jméno může být v "zesnulyJmeno" jako "Jméno Příjmení"
          const full = String(v?.zesnulyJmeno || "").trim();
          let jmeno = String(v?.jmeno || "").trim();
          let prijmeni = String(v?.prijmeni || "").trim();
          if (full && (!jmeno || !prijmeni)) {
            const parts = full.split(/\s+/).filter(Boolean);
            if (!jmeno && parts.length >= 1) jmeno = parts[0];
            if (!prijmeni && parts.length >= 2) prijmeni = parts.slice(1).join(" ");
          }

          hydrated.vstupniInformace = { ...vi };
          hydrated.zesnulaOsoba = { ...zo };

          if (viEmpty) {
            if (!hydrated.vstupniInformace.jmenoZesnuleho && jmeno) hydrated.vstupniInformace.jmenoZesnuleho = jmeno;
            if (!hydrated.vstupniInformace.prijmeniZesnuleho && prijmeni) hydrated.vstupniInformace.prijmeniZesnuleho = prijmeni;
            if (!hydrated.vstupniInformace.mistoUmrti && v?.mistoUmrti) hydrated.vstupniInformace.mistoUmrti = v.mistoUmrti;
            if (!hydrated.vstupniInformace.adresaUmrti && v?.mistoUmrti) hydrated.vstupniInformace.adresaUmrti = v.mistoUmrti;
            if (!hydrated.vstupniInformace.datumUmrti && v?.datumUmrti) hydrated.vstupniInformace.datumUmrti = v.datumUmrti;
            if (!hydrated.vstupniInformace.casUmrti && v?.casUmrti) hydrated.vstupniInformace.casUmrti = v.casUmrti;
          }

          if (zoEmpty) {
            if (!hydrated.zesnulaOsoba.jmeno && jmeno) hydrated.zesnulaOsoba.jmeno = jmeno;
            if (!hydrated.zesnulaOsoba.prijmeni && prijmeni) hydrated.zesnulaOsoba.prijmeni = prijmeni;
            if (!hydrated.zesnulaOsoba.mistoUmrti && v?.mistoUmrti) hydrated.zesnulaOsoba.mistoUmrti = v.mistoUmrti;
            if (!hydrated.zesnulaOsoba.datumUmrti && v?.datumUmrti) hydrated.zesnulaOsoba.datumUmrti = v.datumUmrti;
          }

          // marker, aby bylo vidět že proběhl auto-fill
          hydrated.__autoFilledFromVyjezdy = true;
        }
      } catch (e) {}        try {
          safeSetFormData({ __replace: true, ...hydrated }, "openZakazkaById");
        } catch (e) {
          try { console.error("❌ OPEN APPLY failed", e); } catch {}
        }
        try { __lastOpenTsRef.current = Date.now(); } catch (e) {}
      return { ok: true, id: zid, data: hydrated, raw: resp };
    } catch (e) {
      return { ok: false, id: zid, error: String(e?.message || e) };
    } finally {
        try { setIsOpeningOrder(false); } catch {}
      }
    }, [apiGet, setCurrentOrderId, safeSetFormData, setIsOpeningOrder]);


  
    const knihyApiSeznam = useCallback(async () => {
      // ✅ wrapper na /api/kniha/seznam
      return await apiGet(`/api/kniha/seznam`);
    }, [apiGet]);

const knihyApiNacist = useCallback(
      async (id) => {
        // Vracíme wrapper z openZakazkaById: { ok, id, data, error, status ... }
        return await openZakazkaById(id);
      },
      [openZakazkaById]
    );


  const knihyApiUlozit = useCallback(
    async (payloadIn = {}) => {
      const p = payloadIn && typeof payloadIn === "object" ? payloadIn : {};

      // 🔒 EXPLICIT_SAVE_GUARD (žádné autosave / žádné create při refresh)
      const explicit = p && p.__explicit === true;
      if (!explicit) {
        return { ok: true, skipped: true, reason: "explicit-save-required" };
      }

      
        // 🔒 SAVE_GUARD: nikdy neukládej, když se rozchází currentOrderId a formData.orderId
        const __coid = String(currentOrderId || "").trim();
        const __foid = String(
          (p && (p.id || p.orderId)) ||
          (p && p.formData && p.formData.orderId) ||
          (formData && (formData.orderId || (formData.meta && formData.meta.orderId))) ||
          ""
        ).trim();

        if (__coid && __foid && __coid !== __foid) {
          console.warn("🧨 SAVE_GUARD ORDERID MISMATCH", { currentOrderId: __coid, formOrderId: __foid, payloadId: p?.id, payloadOrderId: p?.orderId });
          return { ok: false, blocked: true, error: "orderId-mismatch-save-guard", currentOrderId: __coid, formOrderId: __foid };
        }
const payload = {
        ...p,
        id: p?.id ? String(p.id).trim() : null,
        author: p?.author || "app",
        formData: p?.formData && typeof p.formData === "object" ? p.formData : formData,
        _setFormData: setFormData,
      };

        // ✅ když není id, vždy vytvoř NOVÝ záznam v knize (neupdate nějaký starý)
        if (!payload.id) payload.__create = true;


      // ✅ FORCE IDS before SAVE: nikdy neukládej staré orderId uvnitř formData


      try {


        const oid = String((payload && (payload.id || payload.orderId)) || currentOrderId || formData?.orderId || formData?.meta?.orderId || formData?.vyjezd?.orderId || "").trim();


        if (oid && payload && payload.formData) {


          payload.formData.orderId = oid;


          payload.formData.id = oid;


          payload.formData.zakazkaId = oid;


          payload.formData.meta = (payload.formData.meta && typeof payload.formData.meta === "object") ? payload.formData.meta : {};


          payload.formData.meta.orderId = oid;


          if (payload.formData.vyjezd && typeof payload.formData.vyjezd === "object") payload.formData.vyjezd.orderId = oid;


          if (payload.formData.vyjezdy) {


            if (Array.isArray(payload.formData.vyjezdy)) payload.formData.vyjezdy.forEach(v => { if (v && typeof v === "object") v.orderId = oid; });


            else if (typeof payload.formData.vyjezdy === "object") payload.formData.vyjezdy.orderId = oid;


          }


          __forceIds(payload.formData);


        }


      } catch (e) {}



      const data = await apiPostJson("/api/kniha/ulozit", payload);



      const newId = String(data?.meta?.id || data?.id || data?.data?.id || "").trim();
        const activeIdAtResponse = String((formDataRef?.current?.orderId || formDataRef?.current?.zakazkaId || formDataRef?.current?.id || currentOrderId || "")).trim();
        if (newId && (!activeIdAtResponse || newId === activeIdAtResponse)) {
          setCurrentOrderId(newId);
        } else if (newId && activeIdAtResponse && newId !== activeIdAtResponse) {
          try { console.warn("⏭️ SKIP stale setCurrentOrderId from save response", { newId, activeIdAtResponse }); } catch {}
        }

      return data?.meta || data;
    },
    [formData]
  );
  // =============================
  // SAVEFLOW_ACTIONS_V2
  // =============================
  const createOrder = useCallback(async () => {
    setSaveError("");
    setIsSaving(true);
    try {

// ✅ FORCE IDS in createOrder (očisti případné staré orderId ve vnitřních strukturách)
let __fdCreate = formData;
try { __fdCreate = __forceIds(structuredClone(formData || {})); } catch (e) { try { __fdCreate = __forceIds(JSON.parse(JSON.stringify(formData || {}))); } catch (e2) {} }
      const data = await apiPostJson("/api/kniha/ulozit", { formData: __fdCreate, __explicit: true, __create: true });
      if (data?.id) setCurrentOrderId(String(data.id));
        setIsEditingExisting(true);
      setLastSavedJson(JSON.stringify(formData || {}));
      return data;
    } catch (e) {
      setSaveError(String(e?.message || e));
      throw e;
    } finally {
      setIsSaving(false);
    }
  }, [formData]);

  const saveOrder = useCallback(async () => {
      // ✅ pokud nejsi v editaci existující zakázky, ULOŽENÍ vždy založí NOVOU (nové ID)
      if (!currentOrderId || !isEditingExisting) {
        return await createOrder();
      }
    setSaveError("");
    setIsSaving(true);
    try {

// ✅ FORCE IDS in saveOrder: před uložením vynucuj orderId = currentOrderId (opraví i vyjezd/vyjezdy v DB)
let __fdSave = formData;
try {
  const __oid = String(currentOrderId || "").trim();
  const __clone = (() => { try { return structuredClone(formData || {}); } catch (e) { return JSON.parse(JSON.stringify(formData || {})); } })();
  if (__oid) {
    __clone.meta = (__clone.meta && typeof __clone.meta === "object") ? __clone.meta : {};
    __clone.meta.orderId = __oid;
    if (__clone.vyjezd && typeof __clone.vyjezd === "object") __clone.vyjezd.orderId = __oid;
    if (__clone.vyjezdy) {
      if (Array.isArray(__clone.vyjezdy)) __clone.vyjezdy.forEach(v => { if (v && typeof v === "object") v.orderId = __oid; });
      else if (typeof __clone.vyjezdy === "object") __clone.vyjezdy.orderId = __oid;
    }
  }
  __fdSave = __forceIds(__clone);
} catch (e) {}
      const data = await apiPostJson("/api/kniha/ulozit", { id: currentOrderId, formData: __fdSave, __explicit: true });
      setLastSavedJson(JSON.stringify(formData || {}));
      return data;
    } catch (e) {
      setSaveError(String(e?.message || e));
      throw e;
    } finally {
      setIsSaving(false);
    }
  }, [currentOrderId, formData, createOrder, isEditingExisting]);

  const resetOrderSession = useCallback(() => {
    setCurrentOrderId("");
    setLastSavedJson("");
    setSaveError("");
    try { localStorage.removeItem("lastSavedJson"); } catch (e) {}
  }, []);



  const knihyApiSmazat = useCallback(
    async (id) => {
      const zid = String(id || "").trim();
      if (!zid) throw new Error("Chybí id");

      await apiDelete(`/api/kniha/smazat/${encodeURIComponent(zid)}`);

      if (currentOrderId === zid) setCurrentOrderId("");
      return true;
    },
    [currentOrderId]
  );

  const knihyApiStav = useCallback(async ({ id, stav }) => {
    const zid = String(id || "").trim();
    if (!zid) throw new Error("Chybí id");

    const data = await apiPostJson("/api/kniha/stav", { id: zid, stav: stav || "rozpracovana" });
    return data?.meta || null;
  }, []);

  // -----------------------------
  // ✅ ENSURE CURRENT ORDER (pro autosave)
  // -----------------------------
  const ensureCurrentOrder = useCallback(async () => {
    // 🔒 SAVEFLOW_V2: po refreshi / během psaní NIC nezakládej.
    // Zakázka vzniká jen ručně přes createOrder().
    return currentOrderId || "";
  }, [currentOrderId]);

  // -----------------------------
  // ULOŽIT OBJEDNÁVKU
  // -----------------------------
  const apiUlozitObjednavku = useCallback(async () => {
    const data = await apiPostJson("/api/ulozit", { formData, currentOrderId: currentOrderId || null });

    const newId = data?.id ? String(data.id) : "";
      const activeIdAtResponse = String((formDataRef?.current?.orderId || formDataRef?.current?.zakazkaId || formDataRef?.current?.id || currentOrderId || "")).trim();
      if (newId && (!activeIdAtResponse || newId === activeIdAtResponse)) {
        setCurrentOrderId(newId);
      } else if (newId && activeIdAtResponse && newId !== activeIdAtResponse) {
        try { console.warn("⏭️ SKIP stale setCurrentOrderId from apiUlozitObjednavku", { newId, activeIdAtResponse }); } catch {}
      }

    return data;
  }, [formData, currentOrderId]);

  // -----------------------------
  // GENEROVAT DOKUMENTY (ZIP)
  // -----------------------------
  const apiGenerujDokumenty = useCallback(
    async ({ vybraneDokumenty, slozkaSablon } = {}) => {
      const body = {
        slozkaSablon: slozkaSablon || formData?.slozkaSablon || "kremace",
  // === AUTO: vybraný výjezd pro propsání do Vstupních informací ===
  vyjezdAuto: null,

        vybraneDokumenty: Array.isArray(vybraneDokumenty) ? vybraneDokumenty : formData?.vybraneDokumenty || [],
        formData,
      };

      const r = await apiPostBlob("/api/generuj-dokumenty", body);
      const ct = String(r.headers.get("content-type") || "").toLowerCase();
      const cd = r.headers.get("content-disposition") || "";

      function getFilenameFromContentDisposition(cdIn) {
        const s = String(cdIn || "");
        const mStar = s.match(/filename\*\=UTF-8''([^;]+)/i);
        if (mStar?.[1]) return decodeURIComponent(mStar[1].trim());
        const m = s.match(/filename\="?([^";]+)"?/i);
        if (m?.[1]) return m[1].trim();
        return "";
      }

      function downloadBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }

      if (ct.includes("application/zip") || ct.includes("application/octet-stream")) {
        const blob = await r.blob();
        const filename = getFilenameFromContentDisposition(cd) || "dokumenty.zip";
        downloadBlob(blob, filename);
        return { ok: true, filename };
      }

      const txt = await r.text().catch(() => "");
      try {
        return txt ? JSON.parse(txt) : { ok: true };
      } catch {
        return { ok: true, raw: txt };
      }
    },
    [formData]
  );

  // -----------------------------
  // SKLAD
  // -----------------------------
  const skladApiNacist = useCallback(async () => {
    const data = await apiGet("/api/sklad");
    return Array.isArray(data?.sklad) ? data.sklad : [];
  }, []);

  const loadSkladFromServer = useCallback(async () => {
    try {
      setSkladError("");
      setSkladLoading(true);
      const sklad = await skladApiNacist();
      setFormData((prev) => ({ ...prev, skladRakvi: Array.isArray(sklad) ? sklad : [] }));
      return sklad;
    } catch (e) {
      setSkladError(e?.message || "Sklad se nepodařilo načíst.");
      return [];
    } finally {
      setSkladLoading(false);
    }
  }, [skladApiNacist]);

  const skladApiReset = useCallback(async () => {
    return await apiPostJson("/api/sklad/reset", {});
  }, []);

  const skladApiOdeber = useCallback(async ({ id, qty = 1, reason = "odečet", orderId = "", zesnuly = "" }) => {
    return await apiPostJson("/api/sklad/odeber", { id, qty, reason, orderId, zesnuly });
  }, []);

  const clearRakve = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      vybranaRakveId: "",
      rakve: {
        ...(prev?.rakve || {}),
        id: "",
        kod: "",
        typRakve: "",
        nazev: "",
        cenaBezDPH: 0,
        sazbaDPH: 0,
      },
    }));
  }, []);

  // -----------------------------
  // VALUE
  // -----------------------------
  const value = useMemo(
    () => ({
          applyObradMetaToOrder,
formData,
      setFormData: safeSetFormData,
      mergeFormData,
      resetForm,
      currentOrderId,
      setCurrentOrderId,

      openZakazkaById,

      knihyApiSeznam,
      knihyApiNacist,
      knihyApiUlozit,
      knihyApiSmazat,
      knihyApiStav,

      ensureCurrentOrder,

      apiUlozitObjednavku,
      apiGenerujDokumenty,

      skladLoading,
      skladError,
      loadSkladFromServer,
      skladApiNacist,
      skladApiReset,
      skladApiOdeber,
      clearRakve,

      // -----------------------------
      // SAVEFLOW (manual save / create)
      // -----------------------------
      createOrder,
      saveOrder,
      resetOrderSession,
      isDirty,
      isSaving,
      saveError,
      lastSavedJson,

      API_BASE_URL,
    }),
    [
      formData,
      mergeFormData,
      resetForm,
      currentOrderId,
      openZakazkaById,
      knihyApiSeznam,
      knihyApiNacist,
      knihyApiUlozit,
      knihyApiSmazat,
      knihyApiStav,
      ensureCurrentOrder,
      apiUlozitObjednavku,
      apiGenerujDokumenty,
      skladLoading,
      skladError,
      loadSkladFromServer,
      skladApiNacist,
      skladApiReset,
      skladApiOdeber,
      clearRakve,
    ]
  );

  return <FormDataContext.Provider value={value}>{children}</FormDataContext.Provider>;
}
